import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  UserPlus,
  Lock,
  RefreshCw,
  Search,
  Copy,
  Check,
  UserCheck,
  UserX,
  Shield,
  Smartphone,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  StatusPill,
} from '../ui/index.js';
import { fetchJson, me } from '../api.js';

interface RbacRole {
  id: string;
  name: string;
  description: string;
  baseRole: 'viewer' | 'reviewer' | 'admin' | 'shop_manager' | 'supply_chain';
  color: string;
  isCustom: boolean;
}

interface RbacData {
  roles: RbacRole[];
}

interface UserRow {
  readonly id: number;
  readonly email: string;
  readonly name: string;
  readonly role: 'viewer' | 'reviewer' | 'admin' | 'shop_manager' | 'supply_chain';
  readonly customRoleId?: string | null;
  readonly carrierScope: string | null;
  readonly brandScope: string | null;
  readonly hasPin?: boolean;
  readonly isActive: boolean;
  readonly lastLoginAt: string | null;
  readonly createdAt: string;
  readonly activeSessions: number;
}

interface UserList {
  readonly users: readonly UserRow[];
}

const ROLE_TONE: Record<UserRow['role'], 'scan' | 'signal' | 'good' | 'muted' | 'alarm'> = {
  admin: 'scan',
  reviewer: 'signal',
  shop_manager: 'good',
  supply_chain: 'signal',
  viewer: 'muted',
};

const ROLE_LABEL: Record<UserRow['role'], string> = {
  admin: 'Administrateur',
  reviewer: 'Reviewer (Achats / Catalogue)',
  shop_manager: 'Shop Manager (Logistique)',
  supply_chain: 'Supply Chain (B2B)',
  viewer: 'Lecteur seul',
};

export function SettingsUsersPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedAudit, setCopiedAudit] = useState(false);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: me });
  const myEmail = meQuery.data?.email ?? null;
  const isAdmin = meQuery.data?.role === 'admin';

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => fetchJson<UserList>('/api/v1/users'),
  });

  const rbacQuery = useQuery({
    queryKey: ['rbac_permissions'],
    queryFn: () => fetchJson<RbacData>('/api/v1/rbac/permissions'),
  });
  const customRoles = rbacQuery.data?.roles.filter((r) => r.isCustom) ?? [];
  const allRoles = rbacQuery.data?.roles ?? [];

  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Add User State
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'reviewer' as UserRow['role'],
    customRoleId: '',
    password: '',
    carrierScope: '',
    brandScope: '',
  });

  // Edit User State
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    role: 'viewer' as UserRow['role'],
    customRoleId: '',
    isActive: true,
    carrierScope: '',
    brandScope: '',
  });

  // Password Reset State
  const [pwTarget, setPwTarget] = useState<UserRow | null>(null);
  const [pwValue, setPwValue] = useState('');

  // PIN Code State
  const [pinTarget, setPinTarget] = useState<UserRow | null>(null);
  const [pinValue, setPinValue] = useState('');

  // Mutations
  const createUser = useMutation({
    mutationFn: (input: typeof newUser) =>
      fetchJson('/api/v1/users', {
        method: 'POST',
        body: JSON.stringify({
          ...input,
          customRoleId: input.customRoleId ? input.customRoleId : undefined,
        }),
      }),
    onSuccess: () => {
      setAddOpen(false);
      setNewUser({ email: '', name: '', role: 'reviewer', customRoleId: '', password: '', carrierScope: '', brandScope: '' });
      setError(null);
      setMessage('Compte opérateur créé avec succès. Transmettez le mot de passe de manière sécurisée.');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['rbac_permissions'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const patchUser = useMutation({
    mutationFn: (input: { id: number } & Partial<typeof editForm>) =>
      fetchJson(`/api/v1/users/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...input,
          customRoleId: input.customRoleId !== undefined ? (input.customRoleId === '' ? null : input.customRoleId) : undefined,
        }),
      }),
    onSuccess: () => {
      setEditingUser(null);
      setError(null);
      setMessage('Profil utilisateur mis à jour.');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['rbac_permissions'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const setUserPassword = useMutation({
    mutationFn: (input: { id: number; password: string }) =>
      fetchJson(`/api/v1/users/${input.id}/password`, {
        method: 'POST',
        body: JSON.stringify({ password: input.password }),
      }),
    onSuccess: () => {
      setPwTarget(null);
      setPwValue('');
      setError(null);
      setMessage('Mot de passe mis à jour. Les sessions précédentes du compte sont révoquées.');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const setUserPin = useMutation({
    mutationFn: (input: { id: number; pin: string | null }) =>
      fetchJson(`/api/v1/users/${input.id}/pin`, {
        method: 'PUT',
        body: JSON.stringify({ pin: input.pin }),
      }),
    onSuccess: (_, input) => {
      setPinTarget(null);
      setPinValue('');
      setError(null);
      setMessage(
        input.pin !== null
          ? 'Code PIN opérateur configuré avec succès.'
          : 'Code PIN opérateur retiré avec succès.',
      );
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const revokeSessions = useMutation({
    mutationFn: (id: number) =>
      fetchJson(`/api/v1/users/${id}/revoke-sessions`, { method: 'POST' }),
    onSuccess: () => {
      setError(null);
      setMessage('Sessions révoquées immédiatement pour ce compte.');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const users = usersQuery.data?.users ?? [];
  const activeCount = users.filter((u) => u.isActive).length;
  const totalSessions = users.reduce((acc, u) => acc + (u.activeSessions || 0), 0);

  const filteredUsers = users.filter((user) => {
    const q = search.toLowerCase();
    const customRole = user.customRoleId ? allRoles.find((r) => r.id === user.customRoleId) : null;
    const customRoleName = customRole ? customRole.name.toLowerCase() : '';
    const matchesSearch =
      q === '' ||
      user.name.toLowerCase().includes(q) ||
      user.email.toLowerCase().includes(q) ||
      user.role.toLowerCase().includes(q) ||
      (customRoleName !== '' && customRoleName.includes(q)) ||
      (user.carrierScope !== null && user.carrierScope.toLowerCase().includes(q)) ||
      (user.brandScope !== null && user.brandScope.toLowerCase().includes(q));

    if (!matchesSearch) return false;
    if (roleFilter !== 'all') {
      if (roleFilter.startsWith('custom:')) {
        const cId = roleFilter.slice(7);
        if (user.customRoleId !== cId) return false;
      } else if (user.role !== roleFilter) {
        return false;
      }
    }
    if (statusFilter === 'active' && !user.isActive) return false;
    if (statusFilter === 'inactive' && user.isActive) return false;
    return true;
  });

  const handleCopyAudit = () => {
    const auditText = users
      .map(
        (u) =>
          `• ${u.name} <${u.email}> — Rôle: ${u.role} — Statut: ${u.isActive ? 'Actif' : 'Désactivé'} — Sessions: ${u.activeSessions} — Dernier login: ${u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('fr-FR') : 'Jamais'}`,
      )
      .join('\n');
    void navigator.clipboard.writeText(auditText);
    setCopiedAudit(true);
    setTimeout(() => setCopiedAudit(false), 2000);
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Utilisateurs & Gestion des Accès"
        hint="Contrôle des comptes d'accès, permissions RBAC, restrictions par transporteur ou marque, et révocation de sessions."
        badge={
          <Badge tone={activeCount > 0 ? 'good' : 'neutral'}>
            {activeCount} / {users.length} actifs
          </Badge>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyAudit}
              className="gap-1.5"
              title="Copier la liste complète pour rapport de sécurité"
            >
              {copiedAudit ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
              <span>{copiedAudit ? 'Copié !' : 'Audit comptes'}</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
              disabled={usersQuery.isFetching}
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${usersQuery.isFetching ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </Button>
            {isAdmin && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  setAddOpen((v) => !v);
                  createUser.reset();
                }}
                className="gap-1.5"
              >
                <UserPlus className="h-4 w-4" />
                <span>{addOpen ? 'Fermer formulaire' : 'Ajouter un utilisateur'}</span>
              </Button>
            )}
          </div>
        }
      />

      {/* Global Notifications */}
      {message !== null && (
        <Alert
          tone="good"
          title="Opération réussie"
          actions={<Button variant="ghost" size="xs" onClick={() => setMessage(null)}>Fermer</Button>}
        >
          {message}
        </Alert>
      )}
      {error !== null && (
        <Alert
          tone="alarm"
          title="Erreur"
          actions={<Button variant="ghost" size="xs" onClick={() => setError(null)}>Fermer</Button>}
        >
          {error}
        </Alert>
      )}

      {/* KPIs Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-ink-ghost text-xs font-semibold">
            <span>Comptes au total</span>
            <Users className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-ink">{users.length}</div>
          <div className="text-[11px] text-ink-soft">Inscrits en base</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-emerald-700 text-xs font-semibold">
            <span>Comptes actifs</span>
            <UserCheck className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-emerald-800">{activeCount}</div>
          <div className="text-[11px] text-emerald-700">Accès autorisés</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-rose-700 text-xs font-semibold">
            <span>Désactivés</span>
            <UserX className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-rose-800">{users.length - activeCount}</div>
          <div className="text-[11px] text-rose-700">Historique préservé</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-sky-700 text-xs font-semibold">
            <span>Sessions en direct</span>
            <Shield className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-sky-800">{totalSessions}</div>
          <div className="text-[11px] text-sky-700">Navigateurs connectés</div>
        </Panel>
      </div>

      {/* Add User Panel (Collapsible) */}
      {addOpen && isAdmin && (
        <Panel className="border-herb-600/30 bg-herb-50/20 shadow-md">
          <PanelBody className="space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-2.5">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-herb-700" />
                <h3 className="text-sm font-bold text-ink">Création d&apos;un nouveau compte opérateur</h3>
              </div>
              <Button type="button" variant="ghost" size="xs" onClick={() => setAddOpen(false)}>
                Fermer
              </Button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createUser.mutate(newUser);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">Nom complet</label>
                  <input
                    type="text"
                    required
                    value={newUser.name}
                    onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))}
                    placeholder="ex. Mohamed Ben Ali"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">Email de connexion</label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))}
                    placeholder="operateur@mapara.tn"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">Rôle / Type de compte</label>
                  <select
                    value={newUser.customRoleId ? `custom:${newUser.customRoleId}` : newUser.role}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val.startsWith('custom:')) {
                        const cId = val.slice(7);
                        const found = customRoles.find((r) => r.id === cId);
                        setNewUser((u) => ({
                          ...u,
                          role: found ? found.baseRole : u.role,
                          customRoleId: cId,
                        }));
                      } else {
                        setNewUser((u) => ({
                          ...u,
                          role: val as UserRow['role'],
                          customRoleId: '',
                        }));
                      }
                    }}
                    className="h-9 w-full rounded-lg border border-border bg-card px-2 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  >
                    <optgroup label="Rôles système standard">
                      <option value="reviewer">Reviewer (Catalogue, GTIN & Prix)</option>
                      <option value="shop_manager">Shop Manager (Expéditions & Commandes)</option>
                      <option value="supply_chain">Supply Chain (Achats Grossistes)</option>
                      <option value="viewer">Viewer (Lecture seule)</option>
                      <option value="admin">Administrateur (Tous les droits)</option>
                    </optgroup>
                    {customRoles.length > 0 && (
                      <optgroup label="Types de comptes personnalisés (RBAC)">
                        {customRoles.map((r) => (
                          <option key={r.id} value={`custom:${r.id}`}>
                            {r.name} (Base API: {r.baseRole})
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">
                    Portail transporteur (Scope restreint)
                  </label>
                  <input
                    type="text"
                    value={newUser.carrierScope}
                    onChange={(e) => setNewUser((u) => ({ ...u, carrierScope: e.target.value }))}
                    placeholder="ex. Jetpack (optionnel)"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  />
                  <span className="text-[10px] text-ink-ghost">Laisse vide pour un accès complet</span>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">
                    Distributeur de marques (Scope restreint)
                  </label>
                  <input
                    type="text"
                    value={newUser.brandScope}
                    onChange={(e) => setNewUser((u) => ({ ...u, brandScope: e.target.value }))}
                    placeholder="ex. ISDIN, SVR (optionnel)"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  />
                  <span className="text-[10px] text-ink-ghost">Laisse vide pour toutes les marques</span>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-ink-soft mb-1">
                    Mot de passe initial (min. 10 car.)
                  </label>
                  <input
                    type="password"
                    required
                    minLength={10}
                    value={newUser.password}
                    onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))}
                    placeholder="••••••••••••"
                    autoComplete="new-password"
                    className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" size="sm" onClick={() => setAddOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" variant="primary" size="sm" disabled={createUser.isPending}>
                  {createUser.isPending ? 'Création en cours…' : 'Créer le compte'}
                </Button>
              </div>

              {createUser.isError && (
                <Alert tone="alarm" title="Échec de création">
                  {String(createUser.error.message)}
                </Alert>
              )}
            </form>
          </PanelBody>
        </Panel>
      )}

      {/* Toolbar / Filters */}
      <Panel>
        <PanelBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-ghost" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer par nom, email, rôle…"
                className="h-8.5 w-full rounded-lg border border-border bg-card pl-8 pr-3 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
              />
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="h-8.5 rounded-lg border border-border bg-card px-2 text-xs font-semibold text-ink"
            >
              <option value="all">Tous les rôles</option>
              <optgroup label="Rôles système standard">
                <option value="admin">Administrateur</option>
                <option value="reviewer">Reviewer</option>
                <option value="shop_manager">Shop Manager</option>
                <option value="supply_chain">Supply Chain</option>
                <option value="viewer">Viewer</option>
              </optgroup>
              {customRoles.length > 0 && (
                <optgroup label="Rôles personnalisés (RBAC)">
                  {customRoles.map((r) => (
                    <option key={r.id} value={`custom:${r.id}`}>
                      {r.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="h-8.5 rounded-lg border border-border bg-card px-2 text-xs font-semibold text-ink"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs uniquement</option>
              <option value="inactive">Désactivés uniquement</option>
            </select>
          </div>

          <span className="text-xs font-semibold text-ink-soft tabular">
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
          </span>
        </PanelBody>
      </Panel>

      {/* Users List */}
      <Panel>
        <PanelBody className="space-y-3">
          <SectionLabel>Comptes Opérateurs ({filteredUsers.length})</SectionLabel>

          {usersQuery.isPending && <Loading label="Lecture de la liste des comptes…" />}
          {usersQuery.isError && (
            <Alert tone="alarm" title="Impossible de charger les utilisateurs">
              {String(usersQuery.error.message)}
            </Alert>
          )}

          {filteredUsers.length === 0 && !usersQuery.isPending && (
            <div className="rounded-xl border border-dashed border-border py-8 text-center text-xs text-ink-soft">
              Aucun utilisateur ne correspond à vos filtres.
            </div>
          )}

          <div className="space-y-2.5">
            {filteredUsers.map((user) => {
              const isSelf = user.email === myEmail;
              return (
                <div
                  key={user.id}
                  className={`rounded-xl border p-3.5 transition-all shadow-2xs ${
                    user.isActive ? 'border-border bg-card' : 'border-slate-300/80 bg-slate-50 opacity-75'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-800 font-bold border border-slate-300">
                        {user.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-ink">{user.name}</span>
                          {isSelf && (
                            <span className="rounded-full bg-sky-100 px-2 py-0.2 text-[10px] font-bold text-sky-800 border border-sky-300">
                              vous
                            </span>
                          )}
                          <StatusPill tone={user.isActive ? 'ok' : 'ko'}>
                            {user.isActive ? 'actif' : 'désactivé'}
                          </StatusPill>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                          <span className="font-mono text-[11px] text-ink-soft">{user.email}</span>
                          <span>·</span>
                          <span className="text-[11px] text-ink-faint">
                            {user.activeSessions} session{user.activeSessions > 1 ? 's' : ''} active{user.activeSessions > 1 ? 's' : ''}
                          </span>
                          <span>·</span>
                          <span className="text-[11px] text-ink-faint">
                            Dernier login :{' '}
                            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('fr-FR') : 'jamais'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Role & Scopes */}
                    <div className="flex flex-wrap items-center gap-2">
                      {(() => {
                        const customRole = user.customRoleId ? allRoles.find((r) => r.id === user.customRoleId) : null;
                        return (
                          <div className="flex flex-col items-start gap-1">
                            <div className="flex items-center gap-1.5">
                              <Badge tone={customRole ? 'good' : ROLE_TONE[user.role]}>
                                {customRole ? customRole.name : ROLE_LABEL[user.role] ?? user.role}
                              </Badge>
                              {customRole && (
                                <span className="rounded bg-slate-100 px-1.5 py-0.2 font-mono text-[9px] text-slate-600 border border-slate-300">
                                  Base: {user.role}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {user.carrierScope !== null && (
                        <Badge tone="scan">Portail transporteur : {user.carrierScope}</Badge>
                      )}
                      {user.brandScope !== null && (
                        <Badge tone="scan">Portail distributeur : {user.brandScope}</Badge>
                      )}

                      {user.hasPin === true && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-300">
                          <Smartphone className="h-3 w-3" /> PIN ✓
                        </span>
                      )}

                      {/* Admin Actions */}
                      {isAdmin && (
                        <div className="flex items-center gap-1.5 ml-2 border-l border-border pl-2">
                          <Button
                            type="button"
                            variant="soft"
                            size="xs"
                            onClick={() => {
                              patchUser.reset();
                              setEditingUser(editingUser?.id === user.id ? null : user);
                              setEditForm({
                                name: user.name,
                                role: user.role,
                                customRoleId: user.customRoleId ?? '',
                                isActive: user.isActive,
                                carrierScope: user.carrierScope ?? '',
                                brandScope: user.brandScope ?? '',
                              });
                            }}
                          >
                            Modifier
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => {
                              setUserPassword.reset();
                              setPwTarget(pwTarget?.id === user.id ? null : user);
                              setPwValue('');
                            }}
                          >
                            Mot de passe
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => {
                              setUserPin.reset();
                              setPinTarget(pinTarget?.id === user.id ? null : user);
                              setPinValue('');
                            }}
                          >
                            PIN
                          </Button>
                          {user.isActive && (
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              disabled={revokeSessions.isPending || (isSelf && user.activeSessions <= 1)}
                              title={isSelf ? 'Votre session en cours est préservée' : 'Déconnecter immédiatement'}
                              onClick={() => revokeSessions.mutate(user.id)}
                            >
                              Révoquer sessions
                            </Button>
                          )}
                          {user.isActive ? (
                            <Button
                              type="button"
                              variant="danger"
                              size="xs"
                              disabled={patchUser.isPending || isSelf}
                              title={isSelf ? 'Impossible de désactiver votre propre compte' : undefined}
                              onClick={() => {
                                if (window.confirm(`Désactiver le compte de ${user.name} ? Ses sessions seront révoquées.`)) {
                                  patchUser.mutate({ id: user.id, isActive: false });
                                }
                              }}
                            >
                              Désactiver
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="primary"
                              size="xs"
                              disabled={patchUser.isPending}
                              onClick={() => patchUser.mutate({ id: user.id, isActive: true })}
                            >
                              Réactiver
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inline Edit Form */}
                  {editingUser?.id === user.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const patchData: Parameters<typeof patchUser.mutate>[0] = {
                          id: user.id,
                          name: editForm.name,
                          role: editForm.role,
                          isActive: editForm.isActive,
                        };
                        if (editForm.carrierScope) patchData.carrierScope = editForm.carrierScope;
                        if (editForm.brandScope) patchData.brandScope = editForm.brandScope;
                        patchUser.mutate(patchData);
                      }}
                      className="mt-3.5 rounded-xl border border-border bg-slate-50/70 p-3.5 space-y-3"
                    >
                      <div className="text-xs font-bold text-ink">Modification du profil de {user.name}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-ink-soft mb-1">Nom complet</label>
                          <input
                            type="text"
                            required
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            className="h-8.5 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-ink-soft mb-1">Rôle / Type de compte</label>
                          <select
                            value={editForm.customRoleId ? `custom:${editForm.customRoleId}` : editForm.role}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.startsWith('custom:')) {
                                const cId = val.slice(7);
                                const found = customRoles.find((r) => r.id === cId);
                                setEditForm((f) => ({
                                  ...f,
                                  role: found ? found.baseRole : f.role,
                                  customRoleId: cId,
                                }));
                              } else {
                                setEditForm((f) => ({
                                  ...f,
                                  role: val as UserRow['role'],
                                  customRoleId: '',
                                }));
                              }
                            }}
                            className="h-8.5 w-full rounded-lg border border-border bg-card px-2 text-xs text-ink focus:border-herb-600 focus:outline-none"
                          >
                            <optgroup label="Rôles système standard">
                              <option value="admin">Administrateur</option>
                              <option value="reviewer">Reviewer</option>
                              <option value="shop_manager">Shop Manager</option>
                              <option value="supply_chain">Supply Chain</option>
                              <option value="viewer">Viewer</option>
                            </optgroup>
                            {customRoles.length > 0 && (
                              <optgroup label="Types de comptes personnalisés (RBAC)">
                                {customRoles.map((r) => (
                                  <option key={r.id} value={`custom:${r.id}`}>
                                    {r.name} (Base API: {r.baseRole})
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-ink-soft mb-1">Portail transporteur (Scope)</label>
                          <input
                            type="text"
                            value={editForm.carrierScope}
                            onChange={(e) => setEditForm((f) => ({ ...f, carrierScope: e.target.value }))}
                            placeholder="vide = aucun filtre"
                            className="h-8.5 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="ghost" size="xs" onClick={() => setEditingUser(null)}>
                          Annuler
                        </Button>
                        <Button type="submit" variant="primary" size="xs" disabled={patchUser.isPending}>
                          {patchUser.isPending ? 'Enregistrement…' : 'Enregistrer'}
                        </Button>
                      </div>
                    </form>
                  )}

                  {/* Inline Password Change Form */}
                  {pwTarget?.id === user.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setUserPassword.mutate({ id: user.id, password: pwValue });
                      }}
                      className="mt-3.5 rounded-xl border border-amber-300 bg-amber-50/50 p-3.5 space-y-3"
                    >
                      <div className="text-xs font-bold text-amber-950 flex items-center gap-1.5">
                        <Lock className="h-3.5 w-3.5 text-amber-700" />
                        <span>Changement de mot de passe pour {user.name}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="password"
                          required
                          minLength={10}
                          value={pwValue}
                          onChange={(e) => setPwValue(e.target.value)}
                          placeholder="Nouveau mot de passe (min 10 car.)"
                          className="h-8.5 w-64 rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                        />
                        <Button type="submit" variant="primary" size="xs" disabled={setUserPassword.isPending}>
                          {setUserPassword.isPending ? 'Mise à jour…' : 'Valider nouveau mot de passe'}
                        </Button>
                        <Button type="button" variant="ghost" size="xs" onClick={() => setPwTarget(null)}>
                          Annuler
                        </Button>
                      </div>
                    </form>
                  )}

                  {/* Inline PIN Code Form */}
                  {pinTarget?.id === user.id && (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const cleaned = pinValue.trim();
                        setUserPin.mutate({ id: user.id, pin: cleaned === '' ? null : cleaned });
                      }}
                      className="mt-3.5 rounded-xl border border-sky-300 bg-sky-50/50 p-3.5 space-y-3"
                    >
                      <div className="text-xs font-bold text-sky-950 flex items-center gap-1.5">
                        <Smartphone className="h-3.5 w-3.5 text-sky-700" />
                        <span>Code PIN opérateur (Shop Manager / Inventaire) pour {user.name}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <input
                          type="password"
                          inputMode="numeric"
                          pattern="[0-9]{4,8}"
                          maxLength={8}
                          value={pinValue}
                          onChange={(e) => setPinValue(e.target.value)}
                          placeholder="Code PIN (4 à 8 chiffres, vide pour retirer)"
                          className="h-8.5 w-64 rounded-lg border border-border bg-card px-2.5 text-xs font-mono text-ink focus:border-herb-600 focus:outline-none"
                        />
                        <Button type="submit" variant="primary" size="xs" disabled={setUserPin.isPending}>
                          {setUserPin.isPending ? 'Enregistrement…' : 'Enregistrer le PIN'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() => {
                            setPinTarget(null);
                            setPinValue('');
                          }}
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              );
            })}
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
